import { ZCDHorizontalBarChartProps } from '@zencity/common-ui';
import { ZCDDataVisualizationColor } from '@zencity/common-ui/lib/ZCD/zcdDataVisualizationColors';
import { SelectedDateRange } from 'types/misc';
import { AspectDivision, AspectScore, AspectScores } from 'types/blockwiseScores';
import { alphaNumericSort, chunkArray } from 'utils/misc';
import {
  MAX_UNREPRESENTATIVE_INDICATOR,
  MIN_UNREPRESENTATIVE_INDICATOR,
} from 'screens/WidgetGenerator/components/UnrepresentativeIndicator/constants';
import { DELIMITER, NEGATIVE_DIFF, NO_CHANGE, NO_COMPARISON, POSITIVE_DIFF } from 'screens/WidgetGenerator/constants';
import i18next from 'i18next';
import { abbreviatePolygonNames, overExtendedPolygonNameExist } from 'screens/WidgetGenerator/helpers/polygonNames';

export type DivisionColor = keyof typeof ZCDDataVisualizationColor;
type ChartDataItem = ZCDHorizontalBarChartProps['chartDatasets'][0]['data'][0];

export const DIVISION_COLORS: DivisionColor[] = [
  'ZCD_DATA_BLUE_70',
  'ZCD_DATA_ORANGE_40',
  'ZCD_DATA_CYAN_50',
  'ZCD_DATA_YELLOW_20',
  'ZCD_DATA_ROSE_60',
  'ZCD_DATA_ROSE_20',
  'ZCD_DATA_GREEN_10',
  'ZCD_DATA_PURPLE_50',
  'ZCD_DATA_BLUE_20',
  'ZCD_DATA_RED_50',
  'ZCD_DATA_YELLOW_50',
  'ZCD_DATA_CYAN_20',
];
const DIVISION_LEVEL_1_DEPTH = 1;
const MAX_LABEL_CHARACTERS = 10;
const DIVISION_GROUPING_NUMBER_THRESHOLD = 6;

const getSelectedCycleAspectScoreIndex = (
  allAspectScores: AspectScores[],
  selectedDateRange: SelectedDateRange,
): number => {
  const selectedCycleAspectScoresIndex = allAspectScores.findIndex(
    (aspectScoresCycle) =>
      aspectScoresCycle.start_date === selectedDateRange.startDate &&
      aspectScoresCycle.end_date === selectedDateRange.endDate,
  );
  if (selectedCycleAspectScoresIndex === -1) {
    throw new Error(
      `Could not find selected cycle Aspect score for date range "${selectedDateRange.startDate} - ${selectedDateRange.endDate}"`,
    );
  }
  return selectedCycleAspectScoresIndex;
};

const findPreviousDivisonAspectScore = (
  previousCycleAspectScores: AspectScore[],
  currentAspectScore: AspectScore,
  divisionId: number,
): { score: number; totalSubmissions: number } | undefined => {
  const previousCycleAspectScore = previousCycleAspectScores.find(
    (aspectScore) => aspectScore.aspect_id === currentAspectScore.aspect_id,
  );
  if (!previousCycleAspectScore) {
    return undefined;
  }
  const { divisions: divisionScores } = previousCycleAspectScore;
  const previousDivisionAspectScore = divisionScores.find((divisionScore) => divisionScore.division_id === divisionId);
  if (!previousDivisionAspectScore) {
    return undefined;
  }
  return { score: previousDivisionAspectScore.score, totalSubmissions: previousDivisionAspectScore.total_submissions };
};

export const generateDataItemLabel = (
  currentDivisionAspectScore: number,
  previousDivisionAspectScore: number,
): string => {
  const scoreDifference = Math.round(currentDivisionAspectScore) - Math.round(previousDivisionAspectScore);
  if (scoreDifference > 0) {
    return ` ${DELIMITER} ${POSITIVE_DIFF} ${scoreDifference}%`;
  }
  if (scoreDifference < 0) {
    return ` ${DELIMITER} ${NEGATIVE_DIFF} ${Math.abs(scoreDifference)}%`;
  }
  return ` ${DELIMITER} ${NO_CHANGE}${scoreDifference}%`;
};

export const getPreviousAspectScoreLabel = (
  allAspectScores: AspectScores[],
  selectedDateRange: SelectedDateRange,
  currentAspectScore: AspectScore,
  currentAspectDivisionScore: AspectDivision,
): string | undefined => {
  const selectedCycleAspectScoresIndex = getSelectedCycleAspectScoreIndex(allAspectScores, selectedDateRange);

  let dataItemLabel = '';
  if (selectedCycleAspectScoresIndex < allAspectScores.length - 1) {
    const previousCycleAspectScores = allAspectScores[selectedCycleAspectScoresIndex + 1].aspect_scores;
    const previousDivisionAspectScore = findPreviousDivisonAspectScore(
      previousCycleAspectScores,
      currentAspectScore,
      currentAspectDivisionScore.division_id,
    );
    if (!previousDivisionAspectScore) {
      return dataItemLabel;
    }
    if (previousDivisionAspectScore.totalSubmissions < MIN_UNREPRESENTATIVE_INDICATOR) {
      dataItemLabel = ` ${DELIMITER} ${NO_COMPARISON}`;
    } else {
      dataItemLabel = generateDataItemLabel(currentAspectDivisionScore.score, previousDivisionAspectScore.score);
    }
  }

  return dataItemLabel;
};

const formatDivisionLabel = (polygonName: string, totalSubmissions: number): string => {
  const displayAsterisk =
    totalSubmissions < MAX_UNREPRESENTATIVE_INDICATOR && totalSubmissions >= MIN_UNREPRESENTATIVE_INDICATOR;
  const asteriskString = displayAsterisk ? '*' : '';
  return polygonName + asteriskString;
};

interface Params {
  allAspectScores: AspectScores[];
  selectedDateRange: SelectedDateRange;
  selectedAspectScore: AspectScore;
  divisionScores: AspectDivision[];
}
export const reduceDivisionScoresToDataItems = (params: Params): ChartDataItem[] => {
  const { allAspectScores, selectedDateRange, selectedAspectScore, divisionScores } = params;
  const divisionIdsToPolygonNames = Object.fromEntries(
    Object.values(divisionScores).map((divisionScore) => [
      divisionScore.division_id,
      divisionScore.division_polygon_name,
    ]),
  );
  // Should we encounter a name that exceeds the required length, we are probably not inclined to modify the entire set
  // of names. However, it is crucial to inspect all names to verify that their uniqueness is preserved.
  const divisionIdsToAbbreviatePolygonNames = overExtendedPolygonNameExist({
    polygonNames: Object.values(divisionIdsToPolygonNames),
    maxCharactersLimit: MAX_LABEL_CHARACTERS,
  })
    ? abbreviatePolygonNames({
        divisionIdsToPolygonNames,
        maxCharactersLimit: MAX_LABEL_CHARACTERS,
      })
    : divisionIdsToPolygonNames;
  return divisionScores
    .filter((divisionScore) => divisionScore.depth === DIVISION_LEVEL_1_DEPTH)
    .map((divisionScore, index) => {
      const { score, total_submissions: totalSubmissions, division_id } = divisionScore;
      const divisionLabel = formatDivisionLabel(divisionIdsToAbbreviatePolygonNames[division_id], totalSubmissions);
      const value = totalSubmissions >= MIN_UNREPRESENTATIVE_INDICATOR ? Math.round(score) : null;
      const dataLabel =
        totalSubmissions >= MIN_UNREPRESENTATIVE_INDICATOR
          ? getPreviousAspectScoreLabel(allAspectScores, selectedDateRange, selectedAspectScore, divisionScore)
          : i18next.t('unrepresentativeIndicator.noScore');
      return {
        label: divisionLabel,
        value,
        backgroundColor: DIVISION_COLORS[index],
        dataLabel,
      };
    })
    .sort((firstDataItem, secondDataItem) => alphaNumericSort(firstDataItem.label, secondDataItem.label));
};

export const groupDataItemsEvenly = (horizontalBarChartDataItems: ChartDataItem[]): ChartDataItem[][] => {
  let chunkSize = DIVISION_GROUPING_NUMBER_THRESHOLD;
  if (horizontalBarChartDataItems.length > DIVISION_GROUPING_NUMBER_THRESHOLD) {
    chunkSize = Math.ceil(horizontalBarChartDataItems.length / 2);
  }

  return chunkArray(horizontalBarChartDataItems, chunkSize);
};
